import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Save, StickyNote } from 'lucide-react';
import { apiGet, apiPost } from '../../utils/api';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import toast from 'react-hot-toast';

/**
 * Converts markdown to clean HTML, then renders it as formatted content.
 * Users only see properly formatted text — never raw markdown.
 * Content is stored as HTML so it displays correctly everywhere.
 */
function markdownToHtml(md) {
  if (!md) return '';
  try {
    const html = marked(md, { breaks: true, gfm: true });
    return DOMPurify.sanitize(html);
  } catch {
    return DOMPurify.sanitize(md);
  }
}

export default function AddToNotesModal({ content, sourceType, sourceId, onClose, theme }) {
  const [title, setTitle] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [category, setCategory] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [folders, setFolders] = useState([]);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  // Convert markdown to HTML on mount
  useEffect(() => {
    setHtmlContent(markdownToHtml(content || ''));
  }, [content]);

  useEffect(() => {
    const loadFolders = async () => {
      try {
        const response = await apiGet('/note_folders');
        if (!response.ok) throw new Error('Failed to load folders');
        const data = await response.json();
        setFolders(data.folders || []);
      } catch (err) {
        // Folders are optional
      }
    };
    loadFolders();
  }, []);

  const handleSave = async () => {
    const currentHtml = editorRef.current?.innerHTML || htmlContent;
    if (!currentHtml.trim() || currentHtml === '<br>') {
      toast.error('Note content is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title,
        content: DOMPurify.sanitize(currentHtml),
        category,
        source_type: sourceType,
        source_id: sourceId,
      };
      if (selectedFolder) payload.note_folder_id = selectedFolder;

      const response = await apiPost('/notes', payload);
      if (!response.ok) throw new Error('Failed to save note');
      const data = await response.json();
      toast.success('Saved to notes');

      // Auto-generate title if none provided
      if (!title && data.note?.id) {
        try {
          const titleRes = await apiPost(`/notes/${data.note.id}/auto_title`);
          if (!titleRes.ok) throw new Error('Title generation failed');
        } catch {
          // Title generation is optional
        }
      }

      onClose();
    } catch (err) {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className={`w-full max-w-lg rounded-xl shadow-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} max-h-[80vh] flex flex-col`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <StickyNote size={18} className="text-blue-500" />
            <h2 className="font-dm-serif font-semibold">Save to Notes</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Note title..."
              className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Content</label>
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              className={`w-full px-4 py-3 rounded-lg border text-sm overflow-y-auto max-h-64 min-h-[120px] outline-none focus:ring-2 focus:ring-blue-500 prose prose-sm max-w-none ${
                theme === 'dark' ? 'bg-gray-700 border-gray-600 prose-invert' : 'bg-gray-50 border-gray-300'
              }`}
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 mb-1">Folder</label>
              <select
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
              >
                <option value="">No folder</option>
                {folders.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., Meeting, Research"
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
              />
            </div>
          </div>

          {sourceType && (
            <div className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
              Source: {sourceType.replace(/_/g, ' ')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-2 px-5 py-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 text-sm rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save Note'}
          </button>
        </div>
      </div>
    </div>
  );
}
